import React from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

function LandingPage() {
  const navigate = useNavigate();
  const appUrl = window.location.origin + '/auth';

  const goToAuth = () => {
    navigate('/auth');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
      {/* Header */}
      <header className="container mx-auto px-4 py-6 flex justify-between items-center">
        <div className="flex flex-col items-center gap-2">
          <img src="/logo-bkb.jpg" alt="Bank Kreiz Breizh" className="w-24 h-24 rounded-xl object-cover shadow-2xl border-2 border-yellow-400" />
          <div className="text-center">
            <h1 className="text-xl font-bold">Banque Kreiz Breizh</h1>
            <p className="text-xs text-gray-300">Banque Centrale de Bretagne</p>
          </div>
        </div>
        <button 
          onClick={goToAuth}
          className="px-6 py-2 bg-yellow-500 text-blue-900 rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
        >
          Connexion
        </button>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-5xl font-bold mb-6 leading-tight">
              Banque Centrale de Bretagne
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Institution financière souveraine pour la gestion des dettes fiscales et services bancaires complets.
            </p>
            <div className="flex gap-4">
              <button 
                onClick={goToAuth}
                className="px-8 py-3 bg-yellow-500 text-blue-900 rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
              >
                Commencer
              </button>
              <button 
                onClick={goToAuth}
                className="px-8 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-blue-900 transition-colors"
              >
                Se connecter
              </button>
            </div>
          </div>
          <div className="flex justify-center">
            <img 
              src="/euro-background.jpg" 
              alt="Symbole Euro avec étoiles européennes" 
              className="rounded-2xl shadow-2xl max-w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* Mobile App Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="bg-blue-900 rounded-3xl p-12">
          <h3 className="text-3xl font-bold mb-4">Application client mobile</h3>
          <p className="text-xl text-gray-300 mb-8">
            Votre banque dans votre poche. Disponible sur iOS et Android.
          </p>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <span className="text-2xl">👤</span>
                  <span>Ouverture de compte en 5 minutes</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-2xl">💳</span>
                  <span>Dépôts et acomptes instantanés</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-2xl">↔️</span>
                  <span>Virements SEPA, PayPal, Alipay, WeChat</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-2xl">📸</span>
                  <span>Encaissement chèques par photo</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-2xl">🔒</span>
                  <span>Face ID / Touch ID sécurisé</span>
                </li>
              </ul>

              <button className="mt-8 w-full px-6 py-4 bg-yellow-500 text-blue-900 rounded-lg font-semibold hover:bg-yellow-400 transition-colors">
                Application Web Mobile - Aucune installation requise !
              </button>
              <p className="text-sm text-gray-400 mt-4">
                Utilisable directement depuis votre navigateur mobile. Applications natives iOS et Android en cours de développement.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 text-center text-blue-900">
              <h4 className="text-2xl font-bold mb-6">Scannez pour accéder</h4>
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="p-4 bg-white rounded-xl shadow-lg mx-auto mb-2 inline-block">
                    <QRCodeSVG 
                      value={appUrl}
                      size={128}
                      level="H"
                      includeMargin={true}
                      bgColor="#ffffff"
                      fgColor="#1e40af"
                    />
                  </div>
                  <p className="font-semibold">Application Web</p>
                  <p className="text-xs text-gray-500">iOS & Android</p>
                </div>
                <div>
                  <div className="p-4 bg-white rounded-xl shadow-lg mx-auto mb-2 inline-block">
                    <QRCodeSVG 
                      value={appUrl}
                      size={128}
                      level="H"
                      includeMargin={true}
                      bgColor="#ffffff"
                      fgColor="#059669"
                    />
                  </div>
                  <p className="font-semibold">Connexion Rapide</p>
                  <p className="text-xs text-gray-500">Tous appareils</p>
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 text-sm">
                <span className="text-lg">🔒</span>
                <span>100% sécurisé - Données chiffrées de bout en bout</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="container mx-auto px-4 py-16">
        <h3 className="text-4xl font-bold text-center mb-12">Nos services</h3>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-blue-900 rounded-2xl p-8 hover:bg-blue-800 transition-colors cursor-pointer">
            <div className="text-5xl mb-4">🏦</div>
            <h4 className="text-2xl font-bold mb-3">Banque de Dépôt</h4>
            <p className="text-gray-300">Gestion complète de comptes, virements et paiements sur toutes plateformes.</p>
          </div>
          <div className="bg-blue-900 rounded-2xl p-8 hover:bg-blue-800 transition-colors cursor-pointer">
            <div className="text-5xl mb-4">📈</div>
            <h4 className="text-2xl font-bold mb-3">Investissements</h4>
            <p className="text-gray-300">Services d'investissement pour professionnels avec gestion de portefeuille.</p>
          </div>
          <div className="bg-blue-900 rounded-2xl p-8 hover:bg-blue-800 transition-colors cursor-pointer">
            <div className="text-5xl mb-4">🛡️</div>
            <h4 className="text-2xl font-bold mb-3">Assurances</h4>
            <p className="text-gray-300">Solutions d'assurance vie, santé, propriété et entreprise.</p>
          </div>
          <div className="bg-blue-900 rounded-2xl p-8 hover:bg-blue-800 transition-colors cursor-pointer">
            <div className="text-5xl mb-4">💱</div>
            <h4 className="text-2xl font-bold mb-3">Livre Bretonne £B</h4>
            <p className="text-gray-300">Transition vers la Livre Bretonne dans 3 ans, taux fixe.</p>
          </div>
          <div className="bg-blue-900 rounded-2xl p-8 hover:bg-blue-800 transition-colors cursor-pointer">
            <div className="text-5xl mb-4">🌍</div>
            <h4 className="text-2xl font-bold mb-3">Paiements internationaux</h4>
            <p className="text-gray-300">Transactions internationales sécurisées et rapides.</p>
          </div>
          <div className="bg-blue-900 rounded-2xl p-8 hover:bg-blue-800 transition-colors cursor-pointer">
            <div className="text-5xl mb-4">👥</div>
            <h4 className="text-2xl font-bold mb-3">Multi-utilisateurs</h4>
            <p className="text-gray-300">Support pour particuliers, entreprises et administrateurs.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-12 mt-16 border-t border-blue-800">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          <div>
            <h4 className="text-xl font-bold mb-4">Bank Kreiz Breizh</h4>
            <p className="text-gray-400 mb-4">
              Banque Centrale de Bretagne - Institution financière souveraine
            </p>
            <div className="flex items-center gap-2 text-gray-300">
              <span>📧</span>
              <a href="mailto:contact@bcb.bzh" className="hover:text-yellow-400 transition-colors">
                contact@bcb.bzh
              </a>
            </div>
          </div>
          <div>
            <h4 className="text-xl font-bold mb-4">Services</h4>
            <ul className="space-y-2 text-gray-400">
              <li>Comptes bancaires</li>
              <li>Crédits & Prêts</li>
              <li>Investissements</li>
              <li>Assurances</li>
              <li>Livre Bretonne £B</li>
            </ul>
          </div>
          <div>
            <h4 className="text-xl font-bold mb-4">Légal</h4>
            <ul className="space-y-2 text-gray-400">
              <li>
                <button onClick={() => window.open('/mentions-legales', '_blank')} className="hover:text-yellow-400 transition-colors">
                  Mentions légales
                </button>
              </li>
              <li>Politique de confidentialité</li>
              <li>Conditions générales</li>
              <li>Protection des données</li>
            </ul>
          </div>
        </div>
        <div className="text-center text-gray-400 pt-8 border-t border-blue-800">
          <p>© 2025 Banque Kreiz Breizh - Banque Centrale de Bretagne. Tous droits réservés.</p>
          <p className="text-sm mt-2">
            Établissement agréé par l'Autorité Bancaire de Bretagne • Capital social: 50.000.000 £B
          </p>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
